import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { openCookieSettings } from "@/components/CookieConsent";

const LEGAL_LINKS = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/refunds", label: "Refund Policy" },
  { to: "/terms", label: "Terms & Conditions" },
];

export const LegalLayout = ({ title, updated, intro, sections = [], testid }) => (
  <div className="min-h-screen bg-white text-neutral-900 font-sans" data-testid={testid}>
    {/* Nav */}
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-neutral-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
        <Link to="/" data-testid="legal-logo-home"><Logo className="h-14 sm:h-16" /></Link>
        <nav className="flex items-center gap-4 sm:gap-6 text-sm font-medium text-neutral-600">
          <Link to="/about" className="hidden sm:inline hover:text-neutral-900 transition-colors">About</Link>
          <Link to="/login" className="hover:text-neutral-900 transition-colors">Log in</Link>
          <Link to="/register"><Button size="sm" className="rounded-full bg-[#516670] hover:bg-[#41545d] gap-1.5">Get started <ArrowRight className="h-3.5 w-3.5" /></Button></Link>
        </nav>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">{title}</h1>
        {updated && <p className="mt-3 text-sm text-neutral-400">Last updated: {updated}</p>}

        {intro && <p className="mt-8 text-neutral-600 leading-relaxed">{intro}</p>}

        <div className="mt-8 space-y-10">
          {sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-heading text-lg font-semibold text-neutral-900">{s.h}</h2>
              <div className="mt-3 space-y-3 text-sm text-neutral-600 leading-relaxed">
                {(s.p || []).map((para, j) => <p key={j}>{para}</p>)}
                {s.list && (
                  <ul className="list-disc pl-5 space-y-1.5">
                    {s.list.map((li, j) => <li key={j}>{li}</li>)}
                  </ul>
                )}
              </div>
            </section>
          ))}
        </div>
      </motion.div>
    </main>

    {/* Footer */}
    <footer className="border-t border-neutral-100 mt-10">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <Link to="/"><Logo className="h-24 sm:h-28" /></Link>
        <p className="text-sm text-neutral-400 order-last sm:order-none">© 2026 Swishr Digital Ltd. All rights reserved.</p>
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-neutral-500">
          <Link to="/about" className="hover:text-neutral-900 transition-colors">About</Link>
          <Link to="/security" className="hover:text-neutral-900 transition-colors">Security</Link>
          {LEGAL_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="hover:text-neutral-900 transition-colors" data-testid={`footer-${l.to.slice(1)}-link`}>{l.label}</Link>
          ))}
          <button onClick={openCookieSettings} className="hover:text-neutral-900 transition-colors" data-testid="footer-cookie-settings">Cookie settings</button>
        </div>
      </div>
    </footer>
  </div>
);

export { LEGAL_LINKS };
